/** Default authenticated console landing — Pivony Advisor full-page experience. */
import {
  hasFullIntelligencePlan,
  hasMarketIntelligencePlan,
  hasSurveyPlan,
  hasSonicProspectPlan,
  hasVoiceOfCustomerPlan,
} from "./entitlements";
import { sanitizeAppReturnPath } from "./routeAccess";

export const CONSOLE_HOME_PATH = "/console/advisor";

const CORE_LEGACY_PACKAGE_IDS = new Set([100, 110, 120, 200, 300]);

/** VoC / Market Intelligence / Full Intelligence orgs land on Advisor, not Sonic Prospect. */
export function hasCorePlatformPlan(subscriptionRow) {
  if (!subscriptionRow) return false;
  if (
    hasVoiceOfCustomerPlan(subscriptionRow) ||
    hasMarketIntelligencePlan(subscriptionRow) ||
    hasFullIntelligencePlan(subscriptionRow)
  ) {
    return true;
  }
  const packageId = Number(subscriptionRow.PackageId);
  return CORE_LEGACY_PACKAGE_IDS.has(packageId);
}

const FREEMIUM_CONSOLE_HOME_PREFIXES = [
  "/console/sonic-prospect",
  "/console/sonic-survey",
  "/console/feedback-capture",
];

function isFreemiumConsoleHomePath(pathname) {
  const base = String(pathname || "")
    .split("?")[0]
    .split("#")[0]
    .replace(/\/+$/, "")
    .toLowerCase();
  return FREEMIUM_CONSOLE_HOME_PREFIXES.some(
    (prefix) => base === prefix || base.startsWith(`${prefix}/`)
  );
}

export const resolveConsoleHomePath = (user, subscriptionRow) => {
  if (hasCorePlatformPlan(subscriptionRow)) {
    return CONSOLE_HOME_PATH;
  }
  if (
    user?.plan === "sonic_prospect" ||
    user?.selectedProduct === "sonic_prospect" ||
    hasSonicProspectPlan(subscriptionRow)
  ) {
    return "/console/sonic-prospect";
  }
  if (
    user?.plan === "survey" ||
    user?.selectedProduct === "survey" ||
    hasSurveyPlan(subscriptionRow)
  ) {
    return "/console/sonic-survey";
  }
  return CONSOLE_HOME_PATH;
};

/** Post-login path: honor returnTo unless it sends core-platform users to freemium homes. */
export function resolveAuthenticatedLandingPath(
  queryParams,
  user,
  subscriptionRow
) {
  const preferredHome = resolveConsoleHomePath(user, subscriptionRow);
  const raw =
    (queryParams && (queryParams.returnTo || queryParams.redirectURL)) || "";
  const returnPath = sanitizeAppReturnPath(raw);
  if (!returnPath) return preferredHome;

  if (
    hasCorePlatformPlan(subscriptionRow) &&
    isFreemiumConsoleHomePath(returnPath)
  ) {
    return preferredHome;
  }

  return returnPath;
}

export const consoleHomeUrl = (origin = window.location.origin, user, subscriptionRow) =>
  `${origin}${resolveConsoleHomePath(user, subscriptionRow)}`;
